import React, { useState } from "react";
import { X, Download, Loader2, Send } from "lucide-react";

import { Payment } from "../../../../types/features/administration/Transaction";
import { generateReceiptPDF } from "../../../../utils/pdfGenerator";
import usePaymentsByUser from "../../../../hooks/administration/transactions/useUserPayments";
import { paymentsService } from "../../../../services/administration/paymentsService";
import UsersTransactionModal from "../users/UsersTrnsactionsModal";

interface TransactionDetailsModalProps {
  transaction: Payment | null;
  isOpen: boolean;
  onClose: () => void;
  onTransactionUpdated?: () => void;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  transaction,
  isOpen,
  onClose,
  onTransactionUpdated,
}) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUserTransactionsModal, setShowUserTransactionsModal] = useState(false);

  const { total: totalTransactionsCount } = usePaymentsByUser({
    userId: transaction?.user_id ?? "",
    enabled: isOpen && !!transaction?.user_id,
  });

  if (!isOpen || !transaction) return null;

  const user: any = transaction.user;

  const handleDownloadReceipt = async () => {
    if (!transaction) return;

    setIsGeneratingPDF(true);
    try {
      await generateReceiptPDF(transaction);
    } catch (error) {
      console.error("Erreur lors de la génération du PDF:", error);
      alert("Erreur lors de la génération du reçu PDF");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleRetryPayment = async () => {
    if (!transaction) return;

    setShowConfirmModal(false);
    setIsRetrying(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Session expirée. Veuillez vous reconnecter.");
        return;
      }

      const response = await paymentsService.retryFailedPayment(token, transaction.id);

      if (response.message) {
        alert(`Succès: ${response.message}`);
        onTransactionUpdated?.();
        onClose();
      }
    } catch (error: any) {
      console.error("Erreur lors de la relance du paiement:", error);
      const errorMessage = error.response?.data?.message || error.message || "Erreur lors de la relance du paiement";
      alert(`Erreur: ${errorMessage}`);
    } finally {
      setIsRetrying(false);
    }
  };

  const shouldShowRetryButton = transaction && transaction.payout_status !== "SUCCESS";

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const renderField = (label: string, value: any) => (
    <div className="flex justify-between items-start">
      <span className="text-gray-600 font-medium">{label}:</span>
      <span className="max-w-xs font-mono text-sm text-right break-all">
        {value === null || value === undefined || value === "" ? "N/A" : String(value)}
      </span>
    </div>
  );

  const InfoSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="mb-3 text-lg font-semibold text-gray-800 border-b pb-2">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );

  const AmountCard = ({
    title,
    amount,
    color,
    currency,
  }: {
    title: string;
    amount: number;
    color: string;
    currency: string;
  }) => (
    <div className={`p-4 bg-${color}-50 rounded-lg border border-${color}-100`}>
      <h4 className="mb-2 text-sm font-medium text-gray-700">{title}</h4>
      <p className={`text-2xl font-bold text-${color}-600`}>
        {amount?.toLocaleString() ?? "0"} {currency}
      </p>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">Détails de la Transaction #{transaction.id.slice(-8)}</h2>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Montants principaux */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <AmountCard title="Montant Total" amount={transaction.total_cad} color="blue" currency="CAD" />
              <AmountCard title="Montant Reçu" amount={transaction.amount_fcfa} color="green" currency="FCFA" />
              <AmountCard title="Frais" amount={transaction.fee_cad} color="orange" currency="CAD" />
            </div>

            {/* Informations générales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoSection title="Informations Générales">
                {renderField("ID Transaction", transaction.id)}
                {renderField("Intent ID", transaction.intent_id)}
                {renderField("Client Secret", transaction.client_secret)}
                {renderField("Charge ID", transaction.charge_id)}
                {renderField("Description", transaction.description)}
                {renderField("Provider", transaction.provider)}
                {renderField("Operator", transaction.operator)}
                {renderField("Phone", transaction.phone)}
                {renderField("Type de paiement", transaction.payment_type)}
                {renderField("Status", transaction.status)}
              </InfoSection>

              <InfoSection title="Informations Utilisateur">
                {user ? (
                  <>
                    {renderField("Nom", `${(user as any).first_name} ${user.last_name}`)}
                    {renderField("Email", (user as any).email)}
                    {renderField("Téléphone", user.phonenumber)}
                    {renderField("Status", user.status)}
                    {renderField("Connexion", user.connexion_type)}
                    {renderField("Rôle", user.role)}
                    {renderField("Créé le", formatDate(user.created_at))}
                    {renderField("MAJ le", formatDate(user.updated_at))}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">Aucun utilisateur lié</p>
                )}
              </InfoSection>
            </div>

            {/* Détails du destinataire */}
            <InfoSection title="Informations du Destinataire">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  {renderField("Nom", transaction.payout_name)}
                  {renderField("Téléphone", transaction.payout_phonenumber)}
                  {renderField("Opérateur", transaction.operator)}
                </div>
                <div className="space-y-2">
                  {renderField("Montant CAD", transaction.amount_cad)}
                  {renderField("Montant FCFA", transaction.amount_fcfa)}
                  {renderField("Taux", transaction.conversion_rate)}
                </div>
              </div>
            </InfoSection>

            {/* Détails du Payout */}
            <InfoSection title="Informations Payout">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  {renderField("Payout ID", transaction.payout_id)}
                  {renderField("Payout Order ID", transaction.payout_order_id)}
                  {renderField("Méthode", transaction.payout_method)}
                  {renderField("Devise", transaction.payout_currency)}
                  {renderField("Montant", transaction.payout_amount)}
                  {renderField("Statut", transaction.payout_status)}
                </div>
                <div className="space-y-2">
                  {renderField("Créé le", formatDate(transaction.payout_created_at))}
                  {renderField("MAJ le", formatDate(transaction.payout_updated_at))}
                </div>
              </div>
            </InfoSection>

            {/* Historique */}
            <InfoSection title="Historique">
              {renderField("Créé le", formatDate(transaction.created_at))}
              {renderField("Dernière mise à jour", formatDate(transaction.updated_at))}
            </InfoSection>

            {/* Statistiques et Actions */}
            <div className="border-t pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Total des transactions</div>
                  <div className="text-3xl font-bold text-gray-800">{totalTransactionsCount}</div>
                  <button
                    className="mt-3 bg-primary text-white px-4 py-2 rounded-lg hover:opacity-90 transition text-sm"
                    onClick={() => setShowUserTransactionsModal(true)}
                  >
                    Voir toutes les transactions de <span className="font-semibold">{user?.first_name}</span>
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleDownloadReceipt}
                  disabled={isGeneratingPDF}
                  className="flex-1 flex justify-center items-center px-4 py-2 space-x-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGeneratingPDF ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Génération...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span>Télécharger le Reçu</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Modal de confirmation */}
        {showConfirmModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Confirmer la relance</h3>
              <p className="text-gray-600 mb-6">
                Êtes-vous sûr de vouloir relancer le transfert pour cette transaction ?
              </p>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2 mb-6">
                <p className="text-sm">
                  <span className="font-semibold">Transaction:</span> #{transaction?.id.slice(-8)}
                </p>
                <p className="text-sm">
                  <span className="font-semibold">Montant:</span> {transaction?.payout_amount}{" "}
                  {transaction?.payout_currency}
                </p>
                <p className="text-sm">
                  <span className="font-semibold">Destinataire:</span> {transaction?.payout_phonenumber}
                </p>
                <p className="text-sm">
                  <span className="font-semibold">Statut actuel:</span>{" "}
                  <span className="text-orange-600 font-semibold">{transaction?.payout_status}</span>
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={handleRetryPayment}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal des transactions de l'utilisateur */}
      <UsersTransactionModal
        isOpen={showUserTransactionsModal}
        onClose={() => {
          setShowUserTransactionsModal(false);
          onTransactionUpdated?.();
        }}
        userId={user?.id || ""}
        userName={user ? `${user.first_name} ${user.last_name}` : undefined}
        onTransactionUpdated={onTransactionUpdated}
      />
    </>
  );
};

export default TransactionDetailsModal;
